// About.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export function About() {
  return (
    <>
      <Helmet defer={false}>
        <title>About Hormonely</title>
        <meta name="description" content="Hormonely provides plain English hormone health information for men and women in their 40s, 50s and beyond. No jargon, no scare tactics, no agenda." />
      </Helmet>
      <div className="page-hero">
        <div className="container page-hero__inner">
          <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>About</div>
          <h1 className="page-hero__title">Why we built <em>Hormonely</em></h1>
        </div>
      </div>
      <section className="section section--white">
        <div className="container">
          <div className="prose">
            <p>Hormonely exists because the information gap around hormonal health is real, significant, and damaging to people's quality of life.</p>
            <p>Women in their 40s are told their exhaustion, brain fog, anxiety and sleeplessness are stress. Men in their 50s are told their bloods are normal. Both are sent home without an explanation, without a plan, and without an understanding of what is actually happening in their body.</p>
            <p>The information exists. The research is there. But it is locked behind clinical language, buried in academic papers, or dressed up in wellness marketing that is difficult to trust.</p>
            <p>Hormonely is an attempt to fix that. Plain English. Both sexes. All decades. No scare tactics. No agenda beyond giving people the information they need to make better decisions about their health.</p>
            <h2>What Hormonely is</h2>
            <p>An information resource. Decade guides, a symptom library, and an AI symptom checker that helps you prepare questions for your GP or pharmacist and points you to the right guide.</p>
            <p>We also sell supplement stacks. They are food supplements, not medicines, and they are not a replacement for a varied diet or for medical advice. Speak to your GP or pharmacist before starting anything new, especially if you take regular medicines.</p>
            <h2>What Hormonely is not</h2>
            <p>A medical service. Nothing on this site is medical advice. We are not doctors and we do not diagnose. If you are experiencing symptoms that affect your quality of life, speak to your GP. If your GP is not receptive, a private specialist is worth considering.</p>
            <h2>Affiliates and transparency</h2>
            <p>Hormonely earns a commission on blood tests booked through Medichecks via our affiliate links. This does not affect the price you pay. We recommend Medichecks because they are the UK's leading home blood testing service and we believe in the value of testing before seeking treatment — not because of the commercial relationship.</p>
            <p>Our supplement stacks are white-label products manufactured by Specialist Supplements Ltd under GMP and ISO quality standards.</p>
            <div style={{ marginTop: '32px' }}>
              <Link to="/symptoms" className="btn-clay">Check your symptoms</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export function Privacy() {
  return (
    <>
      <Helmet defer={false}><title>Privacy Policy | Hormonely</title></Helmet>
      <div className="page-hero">
        <div className="container page-hero__inner">
          <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>Legal</div>
          <h1 className="page-hero__title">Privacy <em>Policy</em></h1>
          <p className="page-hero__sub">Last updated: June 2026</p>
        </div>
      </div>
      <section className="section section--white">
        <div className="container">
          <div className="prose">
            <h2>Who we are</h2>
            <p>Hormonely (hormonely.co.uk) is a hormone health information site operated by Redmaine. If you have questions about this privacy policy, contact us at hello@hormonely.co.uk.</p>
            <h2>What data we collect</h2>
            <p>We collect your email address when you sign up for our free Hormone Symptom Checklist. We do not collect or store health data entered into the symptom checker — this is processed in real time and not retained.</p>
            <h2>How we use your data</h2>
            <p>Your email address is used to send you the symptom checklist you requested and, with your consent, occasional updates from Hormonely. We do not sell your data. We do not share it with third parties other than our email service provider (Resend), with whom we have a data processing agreement in place.</p>
            <h2>Legal basis for processing</h2>
            <p>We process your email address on the basis of consent. You can withdraw consent at any time by unsubscribing using the link in any email we send you.</p>
            <h2>Cookies</h2>
            <p>We use essential cookies only — necessary for the site to function. We do not use advertising cookies or third-party tracking cookies.</p>
            <h2>Your rights</h2>
            <p>Under UK GDPR, you have the right to access, correct or delete any personal data we hold about you. To exercise these rights, contact us at hello@hormonely.co.uk.</p>
            <h2>Third-party links</h2>
            <p>This site contains links to Medichecks and other third-party sites. We are not responsible for the privacy practices of those sites. We recommend reading their privacy policies before providing any personal information.</p>
            <h2>Changes to this policy</h2>
            <p>We may update this policy from time to time. The date at the top of this page indicates when it was last updated.</p>
          </div>
        </div>
      </section>
    </>
  );
}

export function Terms() {
  return (
    <>
      <Helmet defer={false}><title>Terms & Conditions | Hormonely</title></Helmet>
      <div className="page-hero">
        <div className="container page-hero__inner">
          <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>Legal</div>
          <h1 className="page-hero__title">Terms & <em>Conditions</em></h1>
          <p className="page-hero__sub">Last updated: June 2026</p>
        </div>
      </div>
      <section className="section section--white">
        <div className="container">
          <div className="prose">
            <h2>Information only</h2>
            <p>All content on hormonely.co.uk is provided for informational purposes only. Nothing on this site constitutes medical advice, diagnosis or treatment. Always seek the advice of your GP or a qualified health professional before making any changes to your health, starting supplements, or pursuing hormone treatment.</p>
            <h2>Supplement purchases</h2>
            <p>Supplements sold via Stacks by Hormonely are food supplements, not medicines. Food supplements should not replace a varied diet. Speak to your GP or pharmacist before starting anything new, especially if you take regular medicines.</p>
            <p>By purchasing a supplement stack, you confirm that you have read the ingredients list, are not allergic to any of the ingredients, and have consulted your GP if you are pregnant, breastfeeding, or taking prescription medication.</p>
            <h2>Affiliate links</h2>
            <p>Hormonely earns a commission on purchases made through our Medichecks affiliate links. This does not affect the price you pay. We only recommend products and services we believe provide genuine value.</p>
            <h2>AI symptom checker</h2>
            <p>The Hormonely symptom checker is powered by the Anthropic API and provides information only. It does not provide medical advice, diagnose conditions, or recommend specific medical treatments. Always speak to your GP before acting on the output of the symptom checker.</p>
            <h2>Limitation of liability</h2>
            <p>To the fullest extent permitted by law, Hormonely accepts no liability for any loss or damage arising from your use of this site or reliance on its content. You use this site at your own risk.</p>
            <h2>Governing law</h2>
            <p>These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            <h2>Contact</h2>
            <p>For any queries regarding these terms, contact us at hello@hormonely.co.uk.</p>
          </div>
        </div>
      </section>
    </>
  );
}

export function NotFound() {
  return (
    <>
      <Helmet defer={false}><title>Page not found | Hormonely</title></Helmet>
      <div style={{ paddingTop: '140px', paddingBottom: '96px', textAlign: 'center' }}>
        <div className="container">
          <div style={{ fontFamily: 'var(--serif)', fontSize: '96px', color: 'var(--clay-lt)', lineHeight: 1, marginBottom: '24px' }}>404</div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '32px', color: 'var(--bark)', marginBottom: '16px' }}>Page not found</h1>
          <p style={{ fontSize: '16px', color: 'var(--stone)', marginBottom: '32px' }}>The page you are looking for does not exist. Try one of the links below.</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" className="btn-primary">Go home</Link>
            <Link to="/symptoms" className="btn-outline">Check symptoms</Link>
            <Link to="/stacks" className="btn-outline">View stacks</Link>
          </div>
        </div>
      </div>
    </>
  );
}
